<script context="module" lang="ts">
	export type SelectAssetsSaveFilter = 'saved' | 'all';
	export type SelectAssetsAssetFilter = 'image' | '3d' | 'image-upload';
</script>

<script lang="ts">
	import { onMount, onDestroy, afterUpdate } from 'svelte';
	import { page } from '$app/stores';
	import { goto } from '$app/navigation';
	import { analytics } from '$lib/analytics';
	import authStore from '$lib/authStore';
	import { GeneratedImagesClient } from '$lib/generatedImagesClient';
	import type {
		Board,
		GeneratedImage,
		GeneratedPointCloud,
		UploadedImage
	} from '$lib/diffusionApi';
	import { firstNCharacters } from '$lib/textPrompting';
	import { utcTimestampToLocalDate } from '$lib/dateUtils';
	import { isIosDevice } from '$lib/deviceClass';
	import { positionElement } from '$lib/elementPositioning';
	import GridAligner from './GridAligner.svelte';
	import { BoardsClient } from '$lib/boardsClient';
	import UndoableActionNotification from './UndoableActionNotification.svelte';
	import PointCloudClient from '$lib/pointCloudClient';
	import ActionNotification from './ActionNotification.svelte';
	import { UploadedImagesClient } from '$lib/uploadedImagesClient';
	import type { User } from 'firebase/auth';
	import type { PromptAddendumSnapshot } from '$lib/diffusionApi/models/PromptAddendumSnapshot';
	import LibraryAsset3d from './LibraryAsset3d.svelte';
	import LibraryAssetImage from './LibraryAssetImage.svelte';
	import LibraryAssetUploadedImage from './LibraryAssetUploadedImage.svelte';
	import { createDialog } from 'svelte-headlessui';
	import Transition from 'svelte-transition';
	import { browser } from '$app/environment';
	import { darkGradientBlock } from '$lib/inline-svg/darkGradientBlock';
	import type { PresetSnapshot } from '$lib/diffusionApi/models/PresetSnapshot';

	const ASSETS_PER_PAGE = 30;
	const ASSETS_PER_PAGE_JOURNEY = 128;

	let user: User | null | undefined = undefined;

	export let showUploadImage: boolean = false;
	export let saveFilter: SelectAssetsSaveFilter = 'saved';
	export let assetFilter: SelectAssetsAssetFilter = 'image';
	export let showPinControls: boolean = false;
	export let showSelectionBoxes: boolean = true;
	export let assetContainerHeight: string = 'calc(100dvh - 8rem)';
	export let assetContainerScroll: string = 'overflow-y-scroll';
	export let assetContainerBorder: string = 'border border-primary-200 dark:border-zinc-800';
	export let assetContainerBackgroundColor: string = 'bg-primary-50 dark:bg-zinc-900';
	export let showSelectionActions: boolean = true;
	export let clickAssetAction: 'select' | 'view' = 'view';
	export let includeBlankEndRow: boolean = true;
	export let maxCols: number = 5;
	export let boards: Board[] = [];
	export let onlyShowAssetIds: string[] = [];
	export let assetSelectionIds: string[] = [];
	export let journeyId: string | undefined = undefined;
	export let showGenerationDetails: boolean = true;
	export let userIsOwner: boolean = true;
	export let mostRecentFirst: boolean = true;
	export let hasLibraryTabSpacer: boolean = true;
	export let useMasonry: boolean = false;

	let library: HTMLElement;
	let modalContent: HTMLElement | undefined = undefined;
	let textarea: HTMLTextAreaElement | undefined = undefined;
	let undoableActionNotification: UndoableActionNotification;

	let subscriptions: (() => void)[] = [];
	let generatedImagesClient: GeneratedImagesClient = new GeneratedImagesClient('');
	let boardsClient = new BoardsClient('');
	let pointCloudClient: PointCloudClient = new PointCloudClient('');
	let uploadedImagesClient: UploadedImagesClient = new UploadedImagesClient('');
	let observer: IntersectionObserver;
	let actionNotification: ActionNotification;

	let boardSelectorDropdownOpen: boolean = false;
	let lastClickedButton: HTMLElement | null = null;
	let selectedBoard: Board | undefined = boards[0];
	let boardIdToBoardImagePinIdsMap: Map<string, string[]> = new Map();

	let isIOS: boolean = false;
	let loadingNextPage: boolean = false;
	let windowLastScrollY: number = 0;

	let images: GeneratedImage[] = [];
	let promptAddendumSnapshotsForImages: PromptAddendumSnapshot[][] = [];
	let presetSnapshotsForImages: PresetSnapshot[][] = [];
	let htmlImages: HTMLImageElement[] = [];
	let fetchedImages: boolean = false;
	let fetchedAllImages: boolean = false;
	let displayImageIndex: number | undefined = undefined;
	let displayImage: GeneratedImage | undefined = undefined;
	let isLargeDisplayImageLoaded: boolean = false;

	let generatedPointClouds: GeneratedPointCloud[] = [];
	let fetched3d: boolean = false;
	let fetchedAll3d: boolean = false;
	let display3dIndex: number | undefined = undefined;
	let display3d: GeneratedPointCloud | undefined = undefined;
	let pointCloudGenerationIdToImage: { [generationId: string]: HTMLImageElement } = {};
	let Viewer3DComponent: typeof import('./Viewer3D.svelte').default | null = null;
	let Screenshot3dComponent: typeof import('./Screenshot3d.svelte').default | null = null;
	let loading3dComponents: Promise<void> | null = null;

	let uploadedImages: UploadedImage[] = [];
	let htmlUploadedImages: HTMLImageElement[] = [];
	let fetchedUploadedImages: boolean = false;
	let fetchedAllUploadedImages: boolean = false;
	let displayUploadedImageIndex: number | undefined = undefined;
	let displayUploadedImage: UploadedImage | undefined = undefined;

	let loadingImage: HTMLImageElement;

	const displayImageDialog = createDialog({ label: 'Image Modal' });
	const display3dDialog = createDialog({ label: '3D Modal' });
	const displayUploadedImageDialog = createDialog({ label: 'Uploaded Image Modal' });

	async function ensure3dComponents(): Promise<void> {
		if (Viewer3DComponent && Screenshot3dComponent) return;
		loading3dComponents ??= Promise.all([
			import('./Viewer3D.svelte'),
			import('./Screenshot3d.svelte')
		]).then(([viewerModule, screenshotModule]) => {
			Viewer3DComponent = viewerModule.default;
			Screenshot3dComponent = screenshotModule.default;
		});
		await loading3dComponents;
	}

	$: if (assetFilter === '3d' || display3d) {
		void ensure3dComponents();
	}

	$: {
		if (browser) {
			if ($displayImageDialog.expanded) {
				document.body.style.overflowY = 'hidden';
			} else {
				document.body.style.overflowY = 'auto';
			}
		}
	}

	$: {
		if (browser) {
			if ($display3dDialog.expanded) {
				document.body.style.overflowY = 'hidden';
			} else {
				document.body.style.overflowY = 'auto';
			}
		}
	}

	$: {
		if (browser) {
			if ($displayUploadedImageDialog.expanded) {
				document.body.style.overflowY = 'hidden';
			} else {
				document.body.style.overflowY = 'auto';
			}
		}
	}

	const openImageModal = () => {
		windowLastScrollY = window.scrollY;
		displayImageDialog.open();
		setTimeout(() => {
			window.scrollTo(0, windowLastScrollY);
			if (modalContent) {
				modalContent.scrollTop = 0;
			}
			if (textarea) {
				textarea.scrollTop = 0;
			}
		}, 5);
	};

	const closeImageModal = () => {
		displayImageDialog.close();
	};

	const open3dModal = () => {
		windowLastScrollY = window.scrollY;
		display3dDialog.open();
		setTimeout(() => {
			window.scrollTo(0, windowLastScrollY);
		}, 5);
	};

	const close3dModal = () => {
		display3dDialog.close();
	};

	const openUploadedImageModal = () => {
		windowLastScrollY = window.scrollY;
		displayUploadedImageDialog.open();
		setTimeout(() => {
			window.scrollTo(0, windowLastScrollY);
		}, 5);
	};

	const closeUploadedImageModal = () => {
		displayUploadedImageDialog.close();
	};

	const clickBoardSelectorButton = (e: MouseEvent) => {
		e.stopPropagation();
		e.preventDefault();

		const currentButton = e.target as HTMLElement;

		// Find all .pin-controls with the 'active' class and remove it
		const activeControls = document.querySelectorAll(
			'.pin-controls.active, .secondary-button.active'
		);
		activeControls.forEach((control) => control.classList.remove('active'));

		// If the dropdown is open and the same button is clicked, close the dropdown
		if (boardSelectorDropdownOpen && lastClickedButton === currentButton) {
			closeBoardSelectorDropdown();
			lastClickedButton = null; // reset the last clicked button
			return;
		}

		// Update the last clicked button
		lastClickedButton = currentButton;

		// Always open or reposition dropdown on button click
		openBoardSelectorDropdown(e);
		boardSelectorDropdownOpen = true;
	};

	const openBoardSelectorDropdown = (e: MouseEvent) => {
		const x = e.clientX;
		const y = e.clientY;

		const element = document.getElementById(
			'board-selector-dropdown-container'
		) as HTMLInputElement;
		const elementClicked = e.target as HTMLElement;

		element.scrollTop = 0;

		positionElement({ x, y }, element, 'dropdown', elementClicked, { x: 27, y: 0 });

		const closestLibraryImageButton = elementClicked.closest('.library-image-button');
		if (closestLibraryImageButton) {
			const controls = closestLibraryImageButton.querySelector('.pin-controls');
			controls?.classList.add('active');
		}

		const relatedButtonId = (e.target as HTMLElement).dataset.related;
		if (relatedButtonId) {
			const secondaryButton = document.getElementById(relatedButtonId);
			if (secondaryButton) {
				secondaryButton.classList.add('active');
			}
		}
	};

	const closeBoardSelectorDropdown = () => {
		const element = document.getElementById(
			'board-selector-dropdown-container'
		) as HTMLInputElement;
		element.style.top = '-1000px';
		element.style.left = '-1000px';

		const activeControls = document.querySelectorAll('.pin-controls.active');
		activeControls.forEach((control) => control.classList.remove('active'));
		boardSelectorDropdownOpen = false;
	};

	const addLocalPinToSelectedBoard = (pinId: string) => {
		if (selectedBoard === undefined) {
			return;
		}
		boardIdToBoardImagePinIdsMap.set(
			selectedBoard.board_id,
			(boardIdToBoardImagePinIdsMap.get(selectedBoard.board_id) ?? []).concat(pinId)
		);
		boardIdToBoardImagePinIdsMap = boardIdToBoardImagePinIdsMap;
	};

	const removeLocalPinFromSelectedBoard = (pinId: string) => {
		if (selectedBoard === undefined) {
			return;
		}
		boardIdToBoardImagePinIdsMap.set(
			selectedBoard.board_id,
			(boardIdToBoardImagePinIdsMap.get(selectedBoard.board_id) ?? []).filter((id) => id !== pinId)
		);
		boardIdToBoardImagePinIdsMap = boardIdToBoardImagePinIdsMap;
	};

	const createPin = async (image: GeneratedImage) => {
		if (selectedBoard === undefined) {
			return;
		}
		image.saved = true;
		addLocalPinToSelectedBoard(image.generation_id);
		undoableActionNotification.open('Pinned to', async () => {
			if (selectedBoard === undefined) {
				return;
			}
			removeLocalPinFromSelectedBoard(image.generation_id);
			await boardsClient.deleteBoardImagePin({
				board_id: selectedBoard.board_id,
				generation_id: image.generation_id
			});
		});
		await boardsClient.createBoardImagePin({
			board_id: selectedBoard.board_id,
			generation_id: image.generation_id
		});
	};

	const deletePin = async (image: GeneratedImage) => {
		if (selectedBoard === undefined) {
			return;
		}
		removeLocalPinFromSelectedBoard(image.generation_id);
		await boardsClient.deleteBoardImagePin({
			board_id: selectedBoard.board_id,
			generation_id: image.generation_id
		});
	};

	const fetchResults = async () => {
		if (assetFilter === 'image') {
			await fetchImageResults();
		} else if (assetFilter === '3d') {
			await fetch3dResults();
		} else if (assetFilter === 'image-upload') {
			await fetchImageUploadResults();
		}
	};

	const fetchImageResults = async () => {
		const saved = saveFilter === 'saved';
		if (loadingNextPage) return;
		if (fetchedAllImages) return;

		const user = $authStore.user;

		loadingNextPage = true;

		if (showPinControls) {
			if (!user) {
				return;
			}
			boardsClient.setToken((await user.getIdToken()) ?? '');
			const boardImagePins = (await boardsClient.getBoardImagePins()) ?? [];
			boardIdToBoardImagePinIdsMap = new Map();
			for (const boardImagePin of boardImagePins) {
				const boardId = boardImagePin.board_id;
				const pinId = boardImagePin.generation_id ?? (boardImagePin.upload_id as string);
				if (boardIdToBoardImagePinIdsMap.has(boardId)) {
					const pinIds = boardIdToBoardImagePinIdsMap.get(boardId) ?? [];
					pinIds.push(pinId);
					boardIdToBoardImagePinIdsMap.set(boardId, pinIds);
				} else {
					boardIdToBoardImagePinIdsMap.set(boardId, [pinId]);
				}
			}
		}

		if (user) {
			generatedImagesClient.setToken((await user.getIdToken()) ?? '');
		}

		// TODO: change API such that user id is not required, given that we are sending ''
		// and ignoring it in the case of a public journey
		const response = await generatedImagesClient.getGeneratedImages(user?.uid ?? '', saved, {
			skip: images.length,
			limit: journeyId ? ASSETS_PER_PAGE_JOURNEY : ASSETS_PER_PAGE,
			most_recent_first: mostRecentFirst,
			generation_ids: onlyShowAssetIds,
			journey_id: journeyId
		});
		const newImages = response.images;
		const newPromptAddendumSnapshots = response.prompt_addendum_snapshots;
		const newPresetSnapshots = response.preset_snapshots;

		const newHtmlImages = newImages.map((image) => {
			const htmlImage = new Image();
			htmlImage.src = darkGradientBlock;
			// Immediately begin preloading the real image
			const realImage = new Image();
			realImage.src = image.small_image_url;
			realImage.onload = () => {
				// wait a bit to account for svelte optimization which
				// causes these small_image_urls to sometimes load
				// before the darkGradientBlock
				// TODO: optimize further
				setTimeout(() => {
					htmlImage.src = image.small_image_url; // Swap once fully loaded
					htmlImages = [...htmlImages];
				}, 5);
			};

			return htmlImage;
		});

		images = [...images, ...newImages];
		htmlImages = [...htmlImages, ...newHtmlImages];
		promptAddendumSnapshotsForImages = [
			...promptAddendumSnapshotsForImages,
			...newPromptAddendumSnapshots
		];
		presetSnapshotsForImages = [...presetSnapshotsForImages, ...newPresetSnapshots];

		if (newImages.length < (journeyId ? ASSETS_PER_PAGE_JOURNEY : ASSETS_PER_PAGE)) {
			fetchedAllImages = true;
			// filter only the selection asset ids actually present
			assetSelectionIds = assetSelectionIds.filter((assetSelectionId) =>
				images.some((image) => image.generation_id === assetSelectionId)
			);
		}

		fetchedImages = true;
		loadingNextPage = false;
	};

	const fetch3dResults = async () => {
		const saved = saveFilter === 'saved';
		if (loadingNextPage) return;
		if (fetchedAll3d) return;

		loadingNextPage = true;

		if (user) {
			pointCloudClient.setToken((await user.getIdToken()) ?? '');
		}

		const newPointClouds = await pointCloudClient.getGeneratedPointClouds(user?.uid ?? '', saved, {
			skip: generatedPointClouds.length,
			limit: journeyId ? ASSETS_PER_PAGE_JOURNEY : ASSETS_PER_PAGE,
			most_recent_first: mostRecentFirst,
			generation_ids: onlyShowAssetIds,
			journey_id: journeyId
		});

		generatedPointClouds = [...generatedPointClouds, ...newPointClouds];

		if (newPointClouds.length < (journeyId ? ASSETS_PER_PAGE_JOURNEY : ASSETS_PER_PAGE)) {
			fetchedAll3d = true;
			// filter only the selection asset ids actually present
			assetSelectionIds = assetSelectionIds.filter((assetSelectionId) =>
				generatedPointClouds.some((pointCloud) => pointCloud.generation_id === assetSelectionId)
			);
		}

		fetched3d = true;
		loadingNextPage = false;
	};

	const fetchImageUploadResults = async () => {
		if (loadingNextPage) return;
		if (fetchedAllUploadedImages) return;

		loadingNextPage = true;

		if (user) {
			uploadedImagesClient.setToken((await user.getIdToken()) ?? '');
		}

		const newUploadedImages = await uploadedImagesClient.getUploadedImages(user?.uid ?? '', {
			skip: uploadedImages.length,
			limit: journeyId ? ASSETS_PER_PAGE_JOURNEY : ASSETS_PER_PAGE,
			most_recent_first: mostRecentFirst,
			upload_ids: onlyShowAssetIds,
			journey_id: journeyId
		});

		const newHtmlUploadedImages = newUploadedImages.map((uploadedImage) => {
			const htmlImage = new Image();
			htmlImage.src = darkGradientBlock;
			// Immediately begin preloading the real image
			const realImage = new Image();
			realImage.src = uploadedImage.image_url;
			realImage.onload = () => {
				htmlImage.src = uploadedImage.image_url; // Swap once fully loaded
				htmlUploadedImages = [...htmlUploadedImages];
			};

			return htmlImage;
		});

		uploadedImages = [...uploadedImages, ...newUploadedImages];
		htmlUploadedImages = [...htmlUploadedImages, ...newHtmlUploadedImages];

		if (newUploadedImages.length < (journeyId ? ASSETS_PER_PAGE_JOURNEY : ASSETS_PER_PAGE)) {
			fetchedAllUploadedImages = true;
			// filter only the selection asset ids actually present
			assetSelectionIds = assetSelectionIds.filter((assetSelectionId) =>
				uploadedImages.some((uploadedImage) => uploadedImage.upload_id === assetSelectionId)
			);
		}

		fetchedUploadedImages = true;
		loadingNextPage = false;
	};

	const createObserver = () => {
		observer = new IntersectionObserver(handleIntersect, {
			root: library,
			rootMargin: '0px',
			threshold: 0.1
		});
		const target = document.querySelector('.load-more');
		if (target) observer.observe(target);
	};

	const handleIntersect: IntersectionObserverCallback = (entries) => {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				fetchResults();
			}
		});
	};

	const goLeft = () => {
		if (assetFilter === 'image' && displayImageIndex !== undefined && displayImageIndex > 0) {
			displayImageIndex -= 1;
			displayImage = images[displayImageIndex];
			isLargeDisplayImageLoaded = false;
		}

		if (assetFilter === '3d' && display3dIndex !== undefined && display3dIndex > 0) {
			display3dIndex -= 1;
			display3d = generatedPointClouds[display3dIndex];
		}

		if (
			assetFilter === 'image-upload' &&
			displayUploadedImageIndex !== undefined &&
			displayUploadedImageIndex > 0
		) {
			displayUploadedImageIndex -= 1;
			displayUploadedImage = uploadedImages[displayUploadedImageIndex];
		}
	};

	const goRight = () => {
		if (
			assetFilter === 'image' &&
			displayImageIndex !== undefined &&
			displayImageIndex < images.length - 1
		) {
			displayImageIndex += 1;
			displayImage = images[displayImageIndex];
			isLargeDisplayImageLoaded = false;

			// preload when they are almost to the end
			if (displayImageIndex + 4 > images.length) {
				// don't use await so they ux doesn't slow down
				fetchResults();
			}
		}

		if (
			assetFilter === '3d' &&
			display3dIndex !== undefined &&
			display3dIndex < generatedPointClouds.length - 1
		) {
			display3dIndex += 1;
			display3d = generatedPointClouds[display3dIndex];

			// preload when they are almost to the end
			if (display3dIndex + 4 > generatedPointClouds.length) {
				// don't use await so they ux doesn't slow down
				fetchResults();
			}
		}

		if (
			assetFilter === 'image-upload' &&
			displayUploadedImageIndex !== undefined &&
			displayUploadedImageIndex < uploadedImages.length - 1
		) {
			displayUploadedImageIndex += 1;
			displayUploadedImage = uploadedImages[displayUploadedImageIndex];

			// preload when they are almost to the end
			if (displayUploadedImageIndex + 4 > uploadedImages.length) {
				// don't use await so they ux doesn't slow down
				fetchResults();
			}
		}
	};

	const unsaveSelectedItems = async () => {
		if (assetSelectionIds.length === 0) {
			return;
		}
		if (assetFilter === 'image') {
			actionNotification.open(`Unfavorited ${assetSelectionIds.length} images`);
			for (let i = 0; i < images.length; i++) {
				if (assetSelectionIds.includes(images[i].generation_id)) {
					images[i].saved = false;
				}
			}
			await generatedImagesClient.patchGeneratedImages(
				assetSelectionIds.map((generation_id) => ({ generation_id, saved: false }))
			);
		} else if (assetFilter === '3d') {
			actionNotification.open(`Unfavorited ${assetSelectionIds.length} 3D models`);
			for (let i = 0; i < generatedPointClouds.length; i++) {
				if (assetSelectionIds.includes(generatedPointClouds[i].generation_id)) {
					generatedPointClouds[i].saved = false;
				}
			}
			await pointCloudClient.patchGeneratedPointClouds(
				assetSelectionIds.map((generation_id) => ({ generation_id, saved: false }))
			);
		}
	};

	const saveSelectedItems = async () => {
		if (assetSelectionIds.length === 0) {
			return;
		}
		if (assetFilter === 'image') {
			actionNotification.open(`Favorited ${assetSelectionIds.length} images`);
			for (let i = 0; i < images.length; i++) {
				if (assetSelectionIds.includes(images[i].generation_id)) {
					images[i].saved = true;
				}
			}
			await generatedImagesClient.patchGeneratedImages(
				assetSelectionIds.map((generation_id) => ({ generation_id, saved: true }))
			);
		} else if (assetFilter === '3d') {
			actionNotification.open(`Favorited ${assetSelectionIds.length} 3D models`);
			for (let i = 0; i < generatedPointClouds.length; i++) {
				if (assetSelectionIds.includes(generatedPointClouds[i].generation_id)) {
					generatedPointClouds[i].saved = true;
				}
			}
			await pointCloudClient.patchGeneratedPointClouds(
				assetSelectionIds.map((generation_id) => ({ generation_id, saved: true }))
			);
		}
	};

	const deleteSelectedItems = async () => {
		if (assetSelectionIds.length === 0) {
			return;
		}
		if (assetFilter === 'image') {
			if (
				!window.confirm(
					`Permanently delete these ${assetSelectionIds.length} images from your library?`
				)
			) {
				return;
			}
			actionNotification.open(`Deleted ${assetSelectionIds.length} images`);
			await generatedImagesClient.patchGeneratedImages(
				assetSelectionIds.map((generation_id) => ({ generation_id, deleted: true }))
			);
			for (let i = 0; i < images.length; i++) {
				if (assetSelectionIds.includes(images[i].generation_id)) {
					images.splice(i, 1);
					htmlImages.splice(i, 1);
					images = images;
					htmlImages = htmlImages;
				}
			}
		} else if (assetFilter === '3d') {
			if (
				!window.confirm(
					`Permanently delete these ${assetSelectionIds.length} 3D models from your library?`
				)
			) {
				return;
			}
			actionNotification.open(`Deleted ${assetSelectionIds.length} 3D models`);
			await pointCloudClient.patchGeneratedPointClouds(
				assetSelectionIds.map((generation_id) => ({ generation_id, deleted: true }))
			);
			generatedPointClouds = generatedPointClouds.filter(
				(pointCloud) => !assetSelectionIds.includes(pointCloud.generation_id)
			);
		} else if (assetFilter === 'image-upload') {
			if (
				!window.confirm(
					`Permanently delete these ${assetSelectionIds.length} uploaded images from your library?`
				)
			) {
				return;
			}
			actionNotification.open(`Deleted ${assetSelectionIds.length} uploaded images`);
			await uploadedImagesClient.patchUploadedImages(
				assetSelectionIds.map((upload_id) => ({ upload_id, deleted: true }))
			);
			for (let i = 0; i < uploadedImages.length; i++) {
				if (assetSelectionIds.includes(uploadedImages[i].upload_id)) {
					uploadedImages.splice(i, 1);
					htmlUploadedImages.splice(i, 1);
					uploadedImages = uploadedImages;
					htmlUploadedImages = htmlUploadedImages;
				}
			}
		}
		assetSelectionIds = [];
	};

	const onPressDownloadImage = async () => {
		if (!displayImage) {
			return;
		}

		analytics.capture('download_generated_image', {
			user_id: user?.uid,
			journey_id: journeyId,
			is_owner: userIsOwner,
			generation_id: displayImage?.generation_id,
			tiling_mode: displayImage?.tiling_mode,
			model_name: displayImage?.model_name
		});

		generatedImagesClient.patchGeneratedImages([
			{
				generation_id: displayImage.generation_id,
				downloaded: true
			}
		]);

		const imageUrl = displayImage?.image_url;
		const downloadedImageTextPrompt = displayImage?.text_prompt;
		if (imageUrl === undefined || downloadedImageTextPrompt === undefined) {
			return;
		}
		await fetch(imageUrl, {
			method: 'GET',
			mode: 'cors',
			cache: 'no-cache'
		})
			.then((res) => res.blob())
			.then((blob) => {
				const url = URL.createObjectURL(blob);
				const a = document.createElement('a');
				a.href = url;
				a.download = firstNCharacters(downloadedImageTextPrompt, 100) + '.png';
				document.body.appendChild(a);
				a.click();
				a.remove();
			});
	};

	const onPressDeleteImage = async () => {
		if (displayImage === undefined || displayImageIndex === undefined) {
			return;
		}
		if (!window.confirm('Permanently delete this image from your library?')) {
			return;
		}

		await generatedImagesClient.patchGeneratedImages([
			{
				generation_id: displayImage.generation_id,
				deleted: true
			}
		]);
		images = images.filter((image) => image.generation_id !== displayImage?.generation_id);
		htmlImages = htmlImages.filter((_, index) => index !== displayImageIndex);
		if (images.length > 0) {
			// If displayImageIndex is out of bounds, set it to the last image in the array.
			if (displayImageIndex >= images.length) {
				displayImageIndex = images.length - 1;
			}
			displayImage = images[displayImageIndex];
			isLargeDisplayImageLoaded = false;
		} else {
			// If no images left, set displayImage and displayImageIndex to undefined.
			displayImage = undefined;
			displayImageIndex = undefined;
			isLargeDisplayImageLoaded = false;
		}
	};

	const onPressSaveImage = async () => {
		if (!displayImage) {
			return;
		}
		displayImage.saved = !displayImage.saved;
		await generatedImagesClient.patchGeneratedImages([
			{
				generation_id: displayImage.generation_id,
				saved: displayImage.saved
			}
		]);
	};

	const onPressSavePointCloud = async () => {
		if (!display3d) {
			return;
		}
		if (display3d.saved) {
			display3d.saved = false;
			await pointCloudClient.patchGeneratedPointClouds([
				{ generation_id: display3d.generation_id, saved: false }
			]);
		} else {
			display3d.saved = true;
			await pointCloudClient.patchGeneratedPointClouds([
				{ generation_id: display3d.generation_id, saved: true }
			]);
		}
	};

	const onPressDeletePointCloud = () => {
		if (display3d === undefined || display3dIndex === undefined) {
			return;
		}
		if (!window.confirm('Permanently delete this 3D model from your library?')) {
			return;
		}
		pointCloudClient.patchGeneratedPointClouds([
			{ generation_id: display3d.generation_id, deleted: true }
		]);
		generatedPointClouds = generatedPointClouds.filter(
			(_) => _.generation_id !== display3d?.generation_id
		);
		if (generatedPointClouds.length > 0) {
			// If display3dIndex is out of bounds, set it to the last image in the array.
			if (display3dIndex >= generatedPointClouds.length) {
				display3dIndex = generatedPointClouds.length - 1;
			}
			display3d = generatedPointClouds[display3dIndex];
		} else {
			// If no images left, set display3d and display3dIndex to undefined.
			display3d = undefined;
			display3dIndex = undefined;
		}
	};

	const onPressDownloadUploadedImage = async () => {
		analytics.capture('download_uploaded_image', {
			user_id: user?.uid,
			journey_id: journeyId,
			is_owner: userIsOwner,
			upload_id: displayUploadedImage?.upload_id
		});
		const imageUrl = displayUploadedImage?.image_url;
		if (imageUrl === undefined) {
			return;
		}
		await fetch(imageUrl, {
			method: 'GET',
			mode: 'cors',
			cache: 'no-cache'
		})
			.then((res) => res.blob())
			.then((blob) => {
				const url = URL.createObjectURL(blob);
				const a = document.createElement('a');
				a.href = url;
				a.download = 'image.png';
				document.body.appendChild(a);
				a.click();
				a.remove();
			});
	};

	const onPressDeleteUploadedImage = async () => {
		if (displayUploadedImage === undefined || displayUploadedImageIndex === undefined) {
			return;
		}
		if (
			!window.confirm(
				'Permanently delete this uploaded image from your library and anywhere it is linked?'
			)
		) {
			return;
		}

		await uploadedImagesClient.patchUploadedImages([
			{
				upload_id: displayUploadedImage.upload_id,
				deleted: true
			}
		]);
		uploadedImages = uploadedImages.filter(
			(uploadedImage) => uploadedImage.upload_id !== displayUploadedImage?.upload_id
		);
		htmlUploadedImages = htmlUploadedImages.filter(
			(_, index) => index !== displayUploadedImageIndex
		);
		if (uploadedImages.length > 0) {
			// If displayUploadedImageIndex is out of bounds, set it to the last image in the array.
			if (displayUploadedImageIndex >= uploadedImages.length) {
				displayUploadedImageIndex = uploadedImages.length - 1;
			}
			displayUploadedImage = uploadedImages[displayUploadedImageIndex];
		} else {
			// If no images left, set displayUploadedImage and displayUploadedImageIndex to undefined.
			displayUploadedImage = undefined;
			displayUploadedImageIndex = undefined;
		}
	};

	const clearResults = () => {
		images = [];
		htmlImages = [];
		fetchedImages = false;
		fetchedAllImages = false;
		generatedPointClouds = [];
		fetched3d = false;
		fetchedAll3d = false;
		assetSelectionIds = [];
		uploadedImages = [];
		htmlUploadedImages = [];
		fetchedUploadedImages = false;
		fetchedAllUploadedImages = false;
	};

	const handleAssetContainerScroll = (e: Event) => {
		if (assetContainerScroll !== 'overflow-y-scroll') {
			return;
		}
		const target = e.target as HTMLElement;
		const threshold = 2 * 176; // w-44 which is the asset box w/h is 176
		const scrollIsCloseToBottom =
			target.scrollHeight - target.scrollTop - target.clientHeight < threshold;
		if (scrollIsCloseToBottom) {
			fetchResults();
		}
	};

	const handleKeydown = (event: KeyboardEvent) => {
		if (event.key === 'ArrowLeft') {
			goLeft();
		} else if (event.key === 'ArrowRight') {
			goRight();
		}
	};

	const handlePointerUp = (e: PointerEvent) => {
		if (!boardSelectorDropdownOpen) return;

		const dropdown = document.getElementById('board-selector-dropdown-container');
		const elementClicked = e.target as HTMLElement;

		const clickedInsideDropdown = dropdown?.contains(elementClicked);
		const clickedOnButton = elementClicked.closest('.library-image-button');

		if (!clickedInsideDropdown && !clickedOnButton) {
			closeBoardSelectorDropdown();
		}
	};

	const pressUploadImageButton = (e: PointerEvent) => {
		const input = document.createElement('input');
		input.type = 'file';
		input.accept = 'image/*';
		input.style.position = 'absolute';
		input.style.opacity = '0.01'; // Barely visible
		input.style.height = '1px';
		input.style.width = '1px';
		document.body.appendChild(input);

		input.addEventListener('change', function () {
			if (!input.files || input.files.length === 0) {
				setTimeout(() => input.remove(), 500); // Delay the removal
				return;
			}
			const file = input.files[0];
			if (file.type.startsWith('image/')) {
				const reader = new FileReader();
				reader.onload = () => {
					if (typeof reader.result !== 'string') {
						return;
					}
					const newImage = new Image();
					newImage.onload = async () => {
						if (!user) {
							return;
						}
						const maxDimension = 2560;
						let { width, height } = newImage;

						if (width > maxDimension || height > maxDimension) {
							if (width > height) {
								height = Math.round((height * maxDimension) / width);
								width = maxDimension;
							} else {
								width = Math.round((width * maxDimension) / height);
								height = maxDimension;
							}
						}

						const canvas = document.createElement('canvas');
						const ctx = canvas.getContext('2d');
						if (!ctx) {
							return;
						}
						canvas.width = width;
						canvas.height = height;
						ctx.drawImage(newImage, 0, 0, width, height);
						const dataUrl = canvas.toDataURL('image/png');
						const base64Png = dataUrl.split(',')[1];

						uploadedImagesClient.setToken((await user.getIdToken()) ?? '');

						await uploadedImagesClient.createUploadedImage(base64Png);
						const tempAssetSelectionIds = [...assetSelectionIds];
						clearResults();
						assetSelectionIds = tempAssetSelectionIds;
						fetchResults();
					};
					newImage.src = reader.result;
				};
				reader.readAsDataURL(file);
			}
			setTimeout(() => input.remove(), 500); // Delay the removal
		});

		input.click();
	};
	onMount(() => {
		isIOS = isIosDevice();

		// set assetFilter and saveFilter from query params
		const assetFilterParam = $page.url.searchParams.get('assetFilter');
		if (assetFilterParam === 'image' || assetFilterParam === '3d') {
			assetFilter = assetFilterParam;
		}
		const saveFilterParam = $page.url.searchParams.get('saveFilter');
		if (saveFilterParam === 'saved' || saveFilterParam === 'all') {
			saveFilter = saveFilterParam;
		}

		subscriptions.push(
			authStore.subscribe(async (auth) => {
				user = auth.user;
				// not required because of public journeys
				// const user = auth.user;
				// if (user === undefined) {
				// 	return;
				// }

				await fetchResults();
			})
		);

		window.addEventListener('keydown', handleKeydown);
		window.addEventListener('pointerup', handlePointerUp);

		return () => {
			window.removeEventListener('keydown', handleKeydown);
			window.removeEventListener('pointerup', handlePointerUp);
		};
	});

	afterUpdate(() => {
		if (observer) observer.disconnect();
		createObserver();
	});

	onDestroy(() => {
		for (const _ of subscriptions) {
			_();
		}
		if (observer) observer.disconnect();
	});

	$: {
		if (modalContent) {
			modalContent.scrollTop = 0;
		}
		if (textarea) {
			textarea.scrollTop = 0;
		}
		displayImageIndex;
	}
</script>

<div
	bind:this={library}
	class="w-fit h-fit flex flex-col justify-start items-start text-zinc-900 dark:text-zinc-200"
>
	{#if hasLibraryTabSpacer}
		<div class="h-2"></div>
	{/if}
	<div class={`${assetContainerScroll}`}>
		<GridAligner {maxCols} />
	</div>
	{#if assetFilter === 'image'}
		{#if fetchedImages && images.length === 0}
			<div class="h-8"></div>
			{#if saveFilter === 'saved'}
				<div class="text-sm text-zinc-900 dark:text-zinc-200">
					You have no favorited images yet.
				</div>
				<div class="h-4"></div>
				<div class="w-[350px] md:w-[425px] text-sm text-zinc-900 dark:text-zinc-200">
					To favorite an image, press <i class="ml-1 mr-1 fa-regular fa-heart"></i> in the Workspace's
					"Image history" tab after generating an image, or in your Library at any time.
				</div>
			{:else}
				<div class="w-80 text-sm text-zinc-500 dark:text-zinc-200">
					There are no images in your Library yet.
					<div class="h-3"></div>
					Create some images at the Canvas.
				</div>
			{/if}
			<div class="h-4"></div>
			<button
				class="border rounded shadow-md
					bg-primary-50 border-primary-200 hover:brightness-90
					dark:bg-zinc-900 dark:border-zinc-800 dark:hover:brightness-150"
			>
				<a
					href="/canvas"
					class="w-fit h-fit p-4 flex flex-row justify-start items-center text-sm text-zinc-900 dark:text-cyan-400"
				>
					<i class="fa-solid fa-paintbrush mr-2.5"></i>
					Go to canvas
				</a>
			</button>
		{/if}
	{/if}

	{#if assetFilter === '3d'}
		{#if fetched3d && generatedPointClouds.length === 0}
			<div class="h-8"></div>
			<div class="w-[350px] text-sm text-zinc-900 dark:text-zinc-200">
				There are no {saveFilter === 'saved' ? 'favorited' : ''} 3D models in your Library yet.
				<div class="h-3"></div>
				Create some 3D models at the Canvas.
			</div>
			<div class="h-4"></div>
			<button
				class="border rounded shadow-md
					bg-primary-50 border-primary-200 hover:brightness-90
					dark:bg-zinc-900 dark:border-zinc-800 dark:hover:brightness-150"
			>
				<a
					href="/canvas"
					class="w-fit h-fit p-4 flex flex-row justify-start items-center text-sm text-zinc-900 dark:text-cyan-400"
				>
					<i class="fa-solid fa-paintbrush mr-2.5"></i>
					Go to canvas
				</a>
			</button>
		{/if}
	{/if}

	{#if assetFilter === 'image-upload'}
		{#if fetchedUploadedImages && uploadedImages.length === 0}
			<div class="h-8"></div>
			<div class="w-80 text-sm text-zinc-500 dark:text-zinc-200">
				There are no uploaded images in your Library. Press the button below to upload an image.
			</div>
			<div class="h-4"></div>
			<button
				class="border rounded shadow-md
					bg-primary-50 border-primary-200 hover:brightness-90
					dark:bg-zinc-900 dark:border-zinc-800 dark:hover:brightness-150"
				on:pointerup={pressUploadImageButton}
			>
				<div class="w-fit h-16 px-5 flex flex-row justify-start items-center text-sm">
					<i class="fa-solid fa-upload text-zinc-900 dark:text-cyan-400 pr-2.5"></i>
					<span class="underline">Upload an image</span>
				</div>
			</button>
		{/if}
	{/if}

	{#if !fetched3d && !fetchedImages && !fetchedUploadedImages}
		<div class="h-1 w-full shimmer bg-primary-200 dark:bg-zinc-900 rounded"></div>
	{/if}

	<!-- Unified Asset Grid -->
	{#if (assetFilter === 'image' && fetchedImages && images.length > 0) || (assetFilter === '3d' && fetched3d && generatedPointClouds.length > 0) || (assetFilter === 'image-upload' && fetchedUploadedImages && uploadedImages.length > 0)}
		<div class="relative w-fit h-fit">
			<div
				class={`flex flex-col justify-start items-center rounded
				${assetContainerScroll} scroller
				${assetContainerBackgroundColor} ${assetContainerBorder}`}
				style={`height: ${assetContainerHeight}`}
				on:scroll={handleAssetContainerScroll}
			>
				<GridAligner {maxCols} />
				{#if useMasonry}
					{#if assetFilter === 'image'}
						<div
							class={`w-full
							${images.length === 1 ? 'columns-1' : ''}
							${images.length === 2 ? 'columns-2' : ''}
							${images.length > 2 ? 'columns-2 sm:columns-3' : ''}
							${images.length > 2 && maxCols > 3 ? 'md:columns-4' : ''}
							${images.length > 2 && maxCols > 4 ? 'lg:columns-5' : ''}
							gap-[2px]`}
						>
							{#each images as image, index (image.generation_id)}
								<LibraryAssetImage
									bind:images
									bind:htmlImages
									bind:displayImageIndex
									bind:displayImage
									bind:isLargeDisplayImageLoaded
									{index}
									{image}
									bind:assetSelectionIds
									bind:clickAssetAction
									bind:showPinControls
									bind:selectedBoard
									bind:boardIdToBoardImagePinIdsMap
									bind:showSelectionBoxes
									bind:useMasonry
									{createPin}
									{deletePin}
									{clickBoardSelectorButton}
									{openImageModal}
								/>
							{/each}
						</div>
					{:else if assetFilter === 'image-upload'}
						<div
							class={`w-full
							${uploadedImages.length === 1 ? 'columns-1' : ''}
							${uploadedImages.length === 2 ? 'columns-2' : ''}
							${uploadedImages.length > 2 ? 'columns-2 sm:columns-3' : ''}
							${uploadedImages.length > 2 && maxCols > 3 ? 'md:columns-4' : ''}
							${uploadedImages.length > 2 && maxCols > 4 ? 'lg:columns-5' : ''}
							gap-[2px]`}
						>
							{#each uploadedImages as uploadedImage, index (uploadedImage.upload_id)}
								<LibraryAssetUploadedImage
									bind:uploadedImages
									bind:htmlUploadedImages
									bind:displayUploadedImageIndex
									bind:displayUploadedImage
									{index}
									{uploadedImage}
									bind:assetSelectionIds
									bind:clickAssetAction
									bind:showSelectionBoxes
									bind:useMasonry
									{openUploadedImageModal}
								/>
							{/each}
						</div>
					{/if}
				{:else}
					<div
						class={`w-fit h-fit grid grid-cols-2 sm:grid-cols-3
						${maxCols > 3 ? 'md:grid-cols-4' : ''}
						${maxCols > 4 ? 'lg:grid-cols-5' : ''}
						grid-flow-row gap-[2px]`}
					>
						{#if assetFilter === 'image'}
							{#each images as image, index (image.generation_id)}
								<LibraryAssetImage
									bind:images
									bind:htmlImages
									bind:displayImageIndex
									bind:displayImage
									bind:isLargeDisplayImageLoaded
									{index}
									{image}
									bind:assetSelectionIds
									bind:clickAssetAction
									bind:showPinControls
									bind:selectedBoard
									bind:boardIdToBoardImagePinIdsMap
									bind:showSelectionBoxes
									bind:useMasonry
									{createPin}
									{deletePin}
									{clickBoardSelectorButton}
									{openImageModal}
								/>
							{/each}
						{:else if assetFilter === '3d'}
							{#each generatedPointClouds as pointCloud, index}
								<LibraryAsset3d
									bind:generatedPointClouds
									bind:display3dIndex
									bind:display3d
									{index}
									{pointCloud}
									bind:assetSelectionIds
									bind:clickAssetAction
									bind:pointCloudGenerationIdToImage
									bind:showSelectionBoxes
									{open3dModal}
								/>
							{/each}
						{:else if assetFilter === 'image-upload'}
							{#if showUploadImage}
								<div
									class={`group w-44 aspect-w-1 aspect-h-1 block overflow-hidden bg-primary-50 dark:bg-navy-900`}
								>
									<button
										class="absolute flex flex-col justify-center items-center text-sm inset-0 focus:outline-none library-image-button bg-primary-50 dark:bg-navy-900 hover:opacity-75"
										on:pointerup={pressUploadImageButton}
									>
										<i class="fa-solid fa-upload text-zinc-900 dark:text-zinc-200"></i>
										<div class="h-2"></div>
										Upload image
									</button>
								</div>
							{/if}
							{#each uploadedImages as uploadedImage, index (uploadedImage.upload_id)}
								<LibraryAssetUploadedImage
									bind:uploadedImages
									bind:htmlUploadedImages
									bind:displayUploadedImageIndex
									bind:displayUploadedImage
									{index}
									{uploadedImage}
									bind:assetSelectionIds
									bind:clickAssetAction
									bind:showSelectionBoxes
									bind:useMasonry
									{openUploadedImageModal}
								/>
							{/each}
						{/if}
					</div>
				{/if}
				{#if includeBlankEndRow}
					<div class="min-h-44 min-w-44 flex flex-row justify-center items-center">
						{#if loadingNextPage}
							<i class="fa-solid fa-circle-notch fa-spin text-zinc-500"></i>
						{/if}
					</div>
				{/if}
				{#if assetSelectionIds.length > 0}
					<div
						class="absolute bottom-0 right-0 w-fit h-fit p-2 flex flex-row justify-between items-center rounded border border-zinc-800 bg-primary-50 dark:bg-navy-900 text-xs text-zinc-200 z-10"
					>
						<button
							aria-label="Clear selection"
							class="w-12 h-12 flex flex-row justify-center items-center rounded
							bg-primary-50 text-zinc-900 hover:brightness-90 dark:bg-navy-900 dark:text-zinc-400 dark:hover:brightness-150"
							on:click={() => {
								assetSelectionIds = [];
							}}
						>
							<i class="fa fa-x text-base"></i>
						</button>
						<div class="w-8"></div>
						<div class="w-fit h-fit flex flex-col justify-center items-center">
							{#if showSelectionActions}
								<div
									class="h-5 mt-2 flex flex-row justify-center items-center text-zinc-900 dark:text-zinc-200"
								>
									<div class="w-4">
										{assetSelectionIds.length}
									</div>
									<div>selected</div>
								</div>
								<div class="h-8 flex flex-row justify-center items-center text-sm">
									<button
										aria-label="Favorites selected items"
										class="w-8 h-8 flex flex-row justify-center items-center rounded text-zinc-700 dark:text-zinc-400 bg-primary-50 hover:brightness-90 dark:bg-navy-900 dark:hover:brightness-150"
										title="Favorites selected items"
										on:click={saveSelectedItems}
									>
										<i class="fa-solid text-rose-700 fa-heart"></i>
									</button>
									<div class="w-1"></div>
									<button
										aria-label="Unfavorites selected items"
										class="w-8 h-8 flex flex-row justify-center items-center rounded text-zinc-700 dark:text-zinc-400 bg-primary-50 hover:brightness-90 dark:bg-navy-900 dark:hover:brightness-150"
										title="Unfavorites selected items"
										on:click={unsaveSelectedItems}
									>
										<i class="fa-regular fa-heart"></i>
									</button>
									<div class="w-1"></div>
									<button
										aria-label="Deletes selected items"
										class="w-8 h-8 flex flex-row justify-center items-center rounded text-zinc-700 dark:text-zinc-400 bg-primary-50 hover:brightness-90 dark:bg-navy-900 dark:hover:brightness-150"
										title="Deletes selected items"
										on:click={deleteSelectedItems}
									>
										<i class="fa-solid fa-trash"></i>
									</button>
								</div>
							{:else}
								<div class="h-10 flex flex-row justify-center items-center">
									<div class="w-4">
										{assetSelectionIds.length}
									</div>
									<div>selected</div>
									<div class="w-6"></div>
								</div>
							{/if}
						</div>
					</div>
				{/if}
			</div>
		</div>
	{/if}
</div>

<div
	id="board-selector-dropdown-container"
	class="absolute left-[-1000px] top-[-1000px] w-48 h-fit max-h-80 flex flex-col justify-start items-start border
	rounded shadow-lg overflow-y-scroll scroller
	bg-primary-50 border-primary-200
	dark:bg-zinc-900 dark:border-zinc-800"
>
	{#each boards as board, i}
		<button
			class={`w-full h-12 p-3 flex flex-row justify-start items-center hover:brightness-75 dark:hover:brightness-125 ${
				i % 2 === 1 ? `bg-primary-50 dark:bg-zinc-900` : `bg-primary-100 dark:bg-navy-900`
			}`}
			on:click={() => {
				selectedBoard = board;
				undoableActionNotification.close();
				closeBoardSelectorDropdown();
			}}
		>
			<div class="text-xs whitespace-nowrap overflow-x-clip">
				<div class="text-zinc-900 dark:text-zinc-50">{board.name}</div>
			</div>
		</button>
	{/each}
</div>

<div id="undoable-action-notification-container">
	<UndoableActionNotification
		bind:this={undoableActionNotification}
		boardName={selectedBoard?.name ?? ''}
	/>
</div>

<div class={`fixed w-screen h-screen inset-0 ${$displayImageDialog.expanded ? 'z-50' : 'z-[-10]'}`}>
	<Transition show={$displayImageDialog.expanded}>
		<button
			aria-label="Close display image modal"
			class="w-full h-full cursor-default bg-zinc-900 bg-opacity-25"
			on:click={displayImageDialog.close}
		></button>
		<div
			class="fixed top-2 right-2 w-10 h-10 flex flex-row justify-center items-center rounded-3xl cursor-pointer z-40 opacity-50 focus:outline-none"
			on:click={() => {
				closeImageModal();
			}}
			on:keydown={(e) => {
				if (e.key === 'Enter') {
					closeImageModal();
				}
			}}
			role="button"
			tabindex="0"
		>
			<i class="fa-solid fa-x text-xl dark:text-zinc-200 text-zinc-900"></i>
		</div>
		<div use:displayImageDialog.modal>
			<div
				class="fixed bottom-2 left-1/2 -translate-x-1/2 p-2 flex flex-row justify-center items-center rounded-3xl z-40 border
				bg-zinc-100 border-zinc-200
				dark:bg-navy-950 dark:border-zinc-800"
			>
				{#if displayImageIndex !== 0 && displayImageIndex !== undefined}
					<div
						class="w-10 h-10 flex flex-row justify-center items-center rounded-3xl border cursor-pointer
							bg-zinc-200 border-zinc-300 text-zinc-900 hover:brightness-90
							dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:brightness-150"
						on:click={() => {
							goLeft();
						}}
						on:keydown={(e) => {
							if (e.key === 'Enter') {
								goLeft();
							}
						}}
						role="button"
						tabindex="0"
					>
						<i class="fas fa-chevron-left text-xl"></i>
					</div>
				{:else}
					<div
						class="w-10 h-10 flex flex-row justify-center items-center rounded-3xl border
						bg-zinc-200 border-zinc-300 text-zinc-900 opacity-50
						dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:opacity-50"
					>
						<i class="fas fa-chevron-left text-xl"></i>
					</div>
				{/if}
				<div class="w-8"></div>
				<div
					class="w-10 h-10 flex flex-row justify-center items-center rounded-3xl border cursor-pointer
					bg-zinc-200 border-zinc-300 text-zinc-900 hover:brightness-90
					dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:brightness-150"
					on:click={() => {
						closeImageModal();
					}}
					on:keydown={(e) => {
						if (e.key === 'Enter') {
							closeImageModal();
						}
					}}
					role="button"
					tabindex="0"
				>
					<i class="fa-solid fa-x text-xl"></i>
				</div>
				<div class="w-8"></div>
				{#if displayImageIndex !== images.length - 1 && displayImageIndex !== undefined}
					<div
						class="w-10 h-10 flex flex-row justify-center items-center rounded-3xl border cursor-pointer
						bg-zinc-200 border-zinc-300 text-zinc-900 hover:brightness-90
						dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:brightness-150"
						on:click={() => {
							goRight();
						}}
						on:keydown={(e) => {
							if (e.key === 'Enter') {
								goRight();
							}
						}}
						role="button"
						tabindex="0"
					>
						<i class="fas fa-chevron-right text-xl"></i>
					</div>
				{:else}
					<div
						class="w-10 h-10 flex flex-row justify-center items-center rounded-3xl border
						bg-zinc-200 border-zinc-300 text-zinc-900 opacity-50
						dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:opacity-50"
					>
						<i class="fas fa-chevron-right text-xl"></i>
					</div>
				{/if}
			</div>
			<div
				id="modalContent"
				bind:this={modalContent}
				class="fixed top-[5%] left-1/2 w-[95%] h-[95%] -translate-x-1/2 flex flex-col justify-start items-center
				rounded-tl rounded-tr border shadow-md z-30 overflow-y-auto
				bg-primary-50 border-primary-200
				dark:bg-zinc-900 dark:border-zinc-800"
			>
				{#if displayImage}
					<div
						class="w-full min-h-[384px] max-h-[384px] sm:min-h-[512px] sm:max-h-[512px] md:min-h-[768px] md:max-h-[768px] bg-zinc-900 dark:bg-[black] flex flex-row justify-center items-center rounded-tr rounded-tl"
					>
						<!-- Small Image (visible initially) -->
						<img
							src={displayImage.small_image_url}
							alt={displayImage.text_prompt}
							class="h-full object-contain select-none"
							style="display: {isLargeDisplayImageLoaded ? 'none' : 'block'};"
						/>
						<!-- Large Image (hidden initially, displayed when loaded) -->
						<img
							src={displayImage.image_url}
							alt={displayImage.text_prompt}
							class="h-full object-contain select-none"
							style="display: {isLargeDisplayImageLoaded ? 'block' : 'none'};"
							on:load={() => (isLargeDisplayImageLoaded = true)}
						/>
					</div>
					<div
						class="w-full h-fit my-4 flex flex-row justify-start items-start rounded
						bg-primary-50
						dark:bg-zinc-900"
					>
						<div class="w-full h-full flex flex-col justify-center items-start">
							{#if userIsOwner || showGenerationDetails}
								<div
									class="w-full pl-4 pr-4 text-xs sm:text-sm flex flex-col justify-center items-left
									text-zinc-900 dark:text-zinc-100"
								>
									<textarea
										bind:this={textarea}
										class="w-full p-2 max-h-20 text-xs rounded border focus:outline-none focus:ring-0 resize-none shadow-sm
											text-zinc-900 border-primary-200 focus:border-primary-200 bg-primary-25
											dark:text-zinc-300 dark:border-zinc-800 dark:focus:border-zinc-800 dark:bg-navy-950"
										value={displayImage.text_prompt}
										readonly
									></textarea>
									{#if displayImageIndex !== undefined}
										<div class="h-1"></div>
										{#each promptAddendumSnapshotsForImages[displayImageIndex] as promptAddendumSnapshot}
											<div class="h-3"></div>
											<div
												class="flex flex-row justify-start items-center text-xs
											    text-zinc-900 dark:text-zinc-400"
											>
												<div class="w-2"></div>
												<i class="fas fa-feather"></i>
												<div class="w-2"></div>
												{promptAddendumSnapshot.name}
												{#if promptAddendumSnapshot.is_default}
													<div class="w-1 flex-shrink-0"></div>
													ϕ
													<div class="w-1 flex-shrink-0"></div>
												{/if}
											</div>
											<div class="h-1"></div>
											<div
												class="w-fit p-2 max-h-20 rounded scroller text-xs border
												text-zinc-900 border-primary-200 focus:border-primary-200 bg-primary-25
												dark:text-zinc-300 dark:border-zinc-800 dark:focus:border-zinc-800 dark:bg-navy-950"
											>
												{promptAddendumSnapshot.text}
											</div>
										{/each}
										{#each presetSnapshotsForImages[displayImageIndex] as presetSnapshot}
											<div class="h-3"></div>
											<div
												class="flex flex-row justify-start items-center text-xs
											    text-zinc-900 dark:text-zinc-400"
											>
												<div class="w-2"></div>
												<i class="fas fa-rainbow"></i>
												<div class="w-2"></div>
												{presetSnapshot.name}
												{#if presetSnapshot.is_default}
													<div class="w-1 flex-shrink-0"></div>
													ϕ
													<div class="w-1 flex-shrink-0"></div>
												{/if}
												<div class="w-2"></div>
												<div class="border p-2 rounded border-primary-200 dark:border-zinc-800">
													Preset strength: {presetSnapshot.strength * 100}%
												</div>
											</div>
										{/each}
									{/if}
								</div>
								<div class="h-3"></div>
							{/if}
							<div class="w-full h-fit flex flex-row justify-start items-center">
								<a
									class="w-fit h-6 ml-4 flex flex-row justify-start items-center px-2 py-1 text-cyan-600 dark:text-cyan-400 hover:brightness-150 text-xs font-semibold rounded"
									href={`/canvas?imageGenerationId=${displayImage.generation_id}${journeyId ? `&journeyId=${journeyId}` : ''}`}
									on:click={(e) => {
										e.preventDefault();
										analytics.capture('open_in_canvas_generated_image', {
											user_id: user?.uid,
											journey_id: journeyId,
											is_owner: userIsOwner,
											generation_id: displayImage?.generation_id,
											text_prompt: displayImage?.text_prompt
										});
										goto(
											`/canvas?imageGenerationId=${displayImage?.generation_id}${journeyId ? `&journeyId=${journeyId}` : ''}`
										);
									}}
								>
									<i class="fas fa-paintbrush mr-2"></i> Open generated image in canvas
								</a>
							</div>
							<div class="h-4"></div>
							<div class="w-full px-4 flex flex-col justify-start items-start text-xs">
								{#if (userIsOwner || showGenerationDetails) && displayImage.input_image_url && displayImage.small_input_image_url && displayImage.transform_strength && displayImage.transform_strength !== 1.0}
									<img
										src={displayImage.small_input_image_url}
										alt={`${displayImageIndex}`}
										class="w-32 h-32 md:w-44 md:h-44 rounded object-contain border
										border-primary-200
										dark:border-zinc-800"
									/>
									<div class="h-3"></div>
									<a
										class="w-fit h-6 flex flex-row justify-start items-center px-2 py-1 text-cyan-600 dark:text-cyan-400 hover:brightness-150 text-xs font-semibold rounded"
										href={`/canvas?imageInputGenerationId=${displayImage.generation_id}${journeyId ? `&journeyId=${journeyId}` : ''}`}
										on:click={(e) => {
											e.preventDefault();
											analytics.capture('open_in_canvas_generated_image_input', {
												user_id: user?.uid,
												journey_id: journeyId,
												is_owner: userIsOwner,
												generation_id: display3d?.generation_id
											});
											goto(
												`/canvas?imageInputGenerationId=${displayImage?.generation_id}${journeyId ? `&journeyId=${journeyId}` : ''}`
											);
										}}
									>
										<i class="fas fa-paintbrush mr-2"></i> Open input image in canvas
									</a>
									<div class="h-4"></div>
								{/if}
							</div>
							{#if (userIsOwner || showGenerationDetails) && displayImage.transform_strength}
								<div class="flex flex-row justify-center items-center">
									{#if displayImage.model_name === 'Halcyon' || displayImage.model_name === 'Aeolian' || displayImage.model_name === 'Aeolian 2'}
										<div
											class="ml-4 mt-1 p-2 border rounded text-xs
											bg-primary-50 text-zinc-900 border-primary-200
											dark:bg-zinc-900 dark:text-zinc-400 dark:border-zinc-800"
										>
											Model: {displayImage.model_name === 'Aeolian 2'
												? 'Aeolian 2'
												: displayImage.model_name}
										</div>
									{/if}
									<div
										class="mx-4 mt-1 p-2 border rounded text-xs
										bg-primary-50 text-zinc-900 border-primary-200
										dark:bg-zinc-900 dark:text-zinc-400 dark:border-zinc-800"
									>
										Transform strength: {(displayImage.transform_strength * 100).toFixed(0)}%
									</div>
									{#if displayImage.seed}
										<div
											class="mr-4 mt-1 p-2 border rounded text-xs
											bg-primary-50 text-zinc-900 border-primary-200
											dark:bg-zinc-900 dark:text-zinc-400 dark:border-zinc-800"
										>
											Seed: {displayImage.seed}
										</div>
									{/if}
								</div>
								<div class="h-2"></div>
							{/if}
							<div class="w-full px-2 flex flex-row flex-wrap justify-start items-center text-xs">
								{#if userIsOwner || showGenerationDetails}
									<div
										class="mx-2 mt-1 p-2 border rounded text-xs
										bg-primary-50 text-zinc-900 border-primary-200
										dark:bg-zinc-900 dark:text-zinc-400 dark:border-zinc-800"
									>
										<i class="mr-1 fas fa-calendar text-zinc-900 dark:text-zinc-400"></i>
										{utcTimestampToLocalDate(displayImage.created_at).toLocaleString()}
									</div>
								{/if}
								<div
									class="mx-2 mt-1 p-2 border rounded text-xs
									bg-primary-50 text-zinc-900 border-primary-200
									dark:bg-zinc-900 dark:text-zinc-400 dark:border-zinc-800"
								>
									<i class="mr-1 fas fa-expand text-zinc-900 dark:text-zinc-400"></i>
									{displayImage.width} x {displayImage.height}
								</div>
							</div>
							<div class="h-14"></div>
						</div>
						<div class="w-fit h-fit mr-4 flex flex-col justify-start items-end">
							{#if userIsOwner}
								{#if displayImage.saved}
									<button
										aria-label="Unfavorite image"
										class="w-8 h-8 flex flex-row justify-center items-center text-base
											font-semibold border rounded
											bg-primary-50 border-primary-200 text-rose-600 hover:brightness-90
											dark:bg-zinc-800 dark:text-rose-600 dark:hover:brightness-150 dark:border-zinc-700"
										on:click={onPressSaveImage}
										title="Unfavorites this image in your library."
									>
										<i class="fa-solid fa-heart"></i>
									</button>
								{:else}
									<button
										aria-label="Favorite image"
										class="w-8 h-8 flex flex-row justify-center items-center text-base
										font-semibold border rounded
										text-zinc-400 bg-primary-50 border-primary-200 hover:brightness-90
										dark:text-zinc-400 dark:bg-zinc-800 dark:border-zinc-700 dark:hover:brightness-125"
										on:click={onPressSaveImage}
										title="Favorites this image in your library."
									>
										<i class="fa-regular fa-heart"></i>
									</button>
								{/if}
								<div class="h-1"></div>
							{/if}
							{#if !isIOS}
								<button
									aria-label="Download image"
									class="w-8 h-8 flex flex-row justify-center items-center text-sm font-semibold rounded border
										text-zinc-900 bg-primary-50 border-primary-200 hover:brightness-90
										dark:text-zinc-400 dark:bg-zinc-800 dark:border-zinc-700 dark:hover:brightness-125"
									on:click={onPressDownloadImage}
								>
									<i class="fas fa-download"></i>
								</button>
								<div class="h-1"></div>
							{/if}
							{#if userIsOwner}
								<button
									aria-label="Delete image"
									class="w-8 h-8 flex flex-row justify-center items-center text-sm font-semibold rounded border
										text-zinc-900 bg-primary-50 border-primary-200 hover:brightness-90
										dark:text-zinc-400 dark:bg-zinc-800 dark:border-zinc-700 dark:hover:brightness-125"
									on:click={onPressDeleteImage}
								>
									<i class="fas fa-trash"></i>
								</button>
							{/if}
						</div>
					</div>
				{/if}
			</div>
		</div>
	</Transition>
</div>

<div class={`fixed w-screen h-screen inset-0 ${$display3dDialog.expanded ? 'z-50' : 'z-[-10]'}`}>
	<Transition show={$display3dDialog.expanded}>
		<button
			aria-label="Close display 3D modal"
			class="w-full h-full cursor-default bg-zinc-900 bg-opacity-25"
			on:click={display3dDialog.close}
		></button>
		<div
			class="fixed top-2 right-2 w-10 h-10 flex flex-row justify-center items-center rounded-3xl cursor-pointer z-40 opacity-50 focus:outline-none"
			on:click={() => {
				close3dModal();
			}}
			on:keydown={(e) => {
				if (e.key === 'Enter') close3dModal();
			}}
			role="button"
			tabindex="0"
		>
			<i class="fa-solid fa-x text-xl dark:text-zinc-200 text-zinc-900"></i>
		</div>

		<div use:display3dDialog.modal>
			<div
				class="fixed bottom-2 left-1/2 -translate-x-1/2 p-2 flex flex-row justify-center items-center rounded-3xl z-40 border
				bg-zinc-100 border-zinc-200
				dark:bg-navy-950 dark:border-zinc-800 select-none"
			>
				{#if display3dIndex !== 0 && display3dIndex !== undefined}
					<div
						class="w-10 h-10 flex justify-center items-center rounded-3xl border cursor-pointer
						bg-zinc-200 border-zinc-300 text-zinc-900 hover:brightness-90
						dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:brightness-150"
						on:click={goLeft}
						on:keydown={(e) => e.key === 'Enter' && goLeft()}
						role="button"
						tabindex="0"
					>
						<i class="fas fa-chevron-left text-xl"></i>
					</div>
				{:else}
					<div
						class="w-10 h-10 flex justify-center items-center rounded-3xl border
						bg-zinc-200 border-zinc-300 text-zinc-900 opacity-50
						dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:opacity-50"
					>
						<i class="fas fa-chevron-left text-xl"></i>
					</div>
				{/if}
				<div class="w-8"></div>
				<div
					class="w-10 h-10 flex justify-center items-center rounded-3xl border cursor-pointer
					bg-zinc-200 border-zinc-300 text-zinc-900 hover:brightness-90
					dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:brightness-150"
					on:click={close3dModal}
					on:keydown={(e) => e.key === 'Enter' && close3dModal()}
					role="button"
					tabindex="0"
				>
					<i class="fa-solid fa-x text-xl"></i>
				</div>
				<div class="w-8"></div>
				{#if display3dIndex !== generatedPointClouds.length - 1 && display3dIndex !== undefined}
					<div
						class="w-10 h-10 flex justify-center items-center rounded-3xl border cursor-pointer
						bg-zinc-200 border-zinc-300 text-zinc-900 hover:brightness-90
						dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:brightness-150"
						on:click={goRight}
						on:keydown={(e) => e.key === 'Enter' && goRight()}
						role="button"
						tabindex="0"
					>
						<i class="fas fa-chevron-right text-xl"></i>
					</div>
				{:else}
					<div
						class="w-10 h-10 flex justify-center items-center rounded-3xl border
						bg-zinc-200 border-zinc-300 text-zinc-900 opacity-50
						dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:opacity-50"
					>
						<i class="fas fa-chevron-right text-xl"></i>
					</div>
				{/if}
			</div>

			<div
				id="modalContent"
				bind:this={modalContent}
				class="fixed top-[5%] left-1/2 w-[95%] h-[95%] -translate-x-1/2 flex flex-col justify-start items-center
				rounded-tl rounded-tr border shadow-md z-30 overflow-y-auto
				bg-primary-50 border-primary-200
				dark:bg-zinc-900 dark:border-zinc-800"
			>
				{#if display3d}
					<div
						class="w-full min-h-[384px] max-h-[768px] bg-[black] flex justify-center items-center rounded-tl rounded-tr"
					>
						{#if Viewer3DComponent}
							<svelte:component
								this={Viewer3DComponent}
								generatedPointCloud={display3d}
								client={pointCloudClient}
								{user}
								{userIsOwner}
								{journeyId}
							/>
						{/if}
					</div>
					<div
						class="w-full h-fit my-4 flex justify-start items-start rounded
						bg-primary-50
						dark:bg-zinc-900"
					>
						<div class="w-full flex flex-col items-start">
							{#if userIsOwner || showGenerationDetails}
								<div class="w-full px-4 text-xs sm:text-sm">
									<div class="text-xs text-zinc-600 dark:text-zinc-400">Input image</div>
									<div class="h-2"></div>
									<img
										src={display3d.image_url}
										alt={`${display3dIndex}`}
										class="w-32 h-32 md:w-44 md:h-44 rounded object-contain border
										border-primary-200 dark:border-zinc-800"
									/>
									<div class="h-2"></div>
									<a
										class="w-fit h-6 flex flex-row items-center px-2 py-1 text-cyan-600 dark:text-cyan-400 hover:brightness-150 text-xs font-semibold rounded"
										href={`/canvas?image3dGenerationId=${display3d.generation_id}${journeyId ? `&journeyId=${journeyId}` : ''}`}
										on:click={(e) => {
											e.preventDefault();
											analytics.capture('open_in_canvas_generated_3d', {
												user_id: user?.uid,
												journey_id: journeyId,
												is_owner: userIsOwner,
												generation_id: display3d?.generation_id
											});
											goto(
												`/canvas?image3dGenerationId=${display3d?.generation_id}${journeyId ? `&journeyId=${journeyId}` : ''}`
											);
										}}
									>
										<i class="fas fa-paintbrush mr-2"></i>Open input in canvas
									</a>
								</div>
								<div class="h-3"></div>
								<div class="w-full px-2 flex flex-wrap items-center text-xs">
									<div
										class="mx-2 mt-1 p-2 border rounded
										bg-primary-50 text-zinc-900 border-primary-200
										dark:bg-zinc-900 dark:text-zinc-400 dark:border-zinc-800"
									>
										<i class="mr-1 fas fa-calendar"></i>
										{utcTimestampToLocalDate(display3d.created_at).toLocaleString()}
									</div>
								</div>
							{/if}
							<div class="h-14"></div>
						</div>
						<div class="w-fit h-fit mr-4 flex flex-col items-end">
							{#if userIsOwner}
								{#if display3d.saved}
									<button
										aria-label="Unfavorite 3D model"
										class="w-8 h-8 flex justify-center items-center text-base font-semibold border rounded
										bg-primary-50 border-primary-200 text-rose-600 hover:brightness-90
										dark:bg-zinc-800 dark:text-rose-600 dark:hover:brightness-150 dark:border-zinc-700"
										on:click={onPressSavePointCloud}
										title="Unfavorites this 3D model in your library."
									>
										<i class="fa-solid fa-heart"></i>
									</button>
								{:else}
									<button
										aria-label="Favorite 3D model"
										class="w-8 h-8 flex justify-center items-center text-base font-semibold border rounded
										text-zinc-400 bg-primary-50 border-primary-200 hover:brightness-90
										dark:text-zinc-400 dark:bg-zinc-800 dark:border-zinc-700 dark:hover:brightness-125"
										on:click={onPressSavePointCloud}
										title="Favorites this 3D model in your library."
									>
										<i class="fa-regular fa-heart"></i>
									</button>
								{/if}
								<div class="h-1"></div>
								<button
									aria-label="Delete 3D model"
									class="w-8 h-8 flex justify-center items-center text-sm font-semibold border rounded
									text-zinc-900 bg-primary-50 border-primary-200 hover:brightness-90
									dark:text-zinc-400 dark:bg-zinc-800 dark:border-zinc-700 dark:hover:brightness-125"
									on:click={onPressDeletePointCloud}
								>
									<i class="fas fa-trash"></i>
								</button>
							{/if}
						</div>
					</div>
				{/if}
			</div>
		</div>
	</Transition>
</div>

<div
	class={`fixed w-screen h-screen inset-0 ${$displayUploadedImageDialog.expanded ? 'z-50' : 'z-[-10]'}`}
>
	<Transition show={$displayUploadedImageDialog.expanded}>
		<button
			aria-label="Close display uploaded image modal"
			class="w-full h-full bg-zinc-900 bg-opacity-25 cursor-default"
			on:click={displayUploadedImageDialog.close}
		></button>
		<div
			class="fixed top-2 right-2 w-10 h-10 flex flex-row justify-center items-center rounded-3xl cursor-pointer z-40 opacity-50 focus:outline-none"
			on:click={() => {
				closeUploadedImageModal();
			}}
			on:keydown={(e) => {
				if (e.key === 'Enter') {
					closeUploadedImageModal();
				}
			}}
			role="button"
			tabindex="0"
		>
			<i class="fa-solid fa-x text-xl dark:text-zinc-200 text-zinc-900"></i>
		</div>

		<div use:displayUploadedImageDialog.modal>
			<div
				class="fixed bottom-2 left-1/2 -translate-x-1/2 p-2 flex flex-row justify-center items-center rounded-3xl z-40 border
				bg-zinc-100 border-zinc-200
				dark:bg-navy-950 dark:border-zinc-800"
			>
				{#if displayUploadedImageIndex !== 0 && displayUploadedImageIndex !== undefined}
					<div
						class="w-10 h-10 flex flex-row justify-center items-center rounded-3xl border cursor-pointer
						bg-zinc-200 border-zinc-300 text-zinc-900 hover:brightness-90
						dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:brightness-150"
						on:click={() => {
							goLeft();
						}}
						on:keydown={(e) => {
							if (e.key === 'Enter') {
								goLeft();
							}
						}}
						role="button"
						tabindex="0"
					>
						<i class="fas fa-chevron-left text-xl"></i>
					</div>
				{:else}
					<div
						class="w-10 h-10 flex flex-row justify-center items-center rounded-3xl border
						bg-zinc-200 border-zinc-300 text-zinc-900 opacity-50
						dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:opacity-50"
					>
						<i class="fas fa-chevron-left text-xl"></i>
					</div>
				{/if}
				<div class="w-8"></div>
				<div
					class="w-10 h-10 flex flex-row justify-center items-center rounded-3xl border cursor-pointer
					bg-zinc-200 border-zinc-300 text-zinc-900 hover:brightness-90
					dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:brightness-150"
					on:click={() => {
						closeUploadedImageModal();
					}}
					on:keydown={(e) => {
						if (e.key === 'Enter') {
							closeUploadedImageModal();
						}
					}}
					role="button"
					tabindex="0"
				>
					<i class="fa-solid fa-x text-xl"></i>
				</div>
				<div class="w-8"></div>
				{#if displayUploadedImageIndex !== uploadedImages.length - 1 && displayUploadedImageIndex !== undefined}
					<div
						class="w-10 h-10 flex flex-row justify-center items-center rounded-3xl border cursor-pointer
						bg-zinc-200 border-zinc-300 text-zinc-900 hover:brightness-90
						dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:brightness-150"
						on:click={() => {
							goRight();
						}}
						on:keydown={(e) => {
							if (e.key === 'Enter') {
								goRight();
							}
						}}
						role="button"
						tabindex="0"
					>
						<i class="fas fa-chevron-right text-xl"></i>
					</div>
				{:else}
					<div
						class="w-10 h-10 flex flex-row justify-center items-center rounded-3xl border
						bg-zinc-200 border-zinc-300 text-zinc-900 opacity-50
						dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:opacity-50"
					>
						<i class="fas fa-chevron-right text-xl"></i>
					</div>
				{/if}
			</div>
			<div
				id="modalContent"
				bind:this={modalContent}
				class="fixed top-[5%] left-1/2 w-[95%] h-[95%] -translate-x-1/2 flex flex-col justify-start items-center
				rounded-tl rounded-tr border shadow-md z-30 overflow-y-auto
				bg-primary-50 border-primary-200
				dark:bg-zinc-900 dark:border-zinc-800"
			>
				{#if displayUploadedImage}
					<div
						class="w-full min-h-[384px] max-h-[384px] sm:min-h-[512px] sm:max-h-[512px] md:min-h-[768px] md:max-h-[768px] bg-zinc-900 dark:bg-[black] flex flex-row justify-center items-center rounded-tr rounded-tl"
					>
						<img
							src={displayUploadedImage.image_url}
							class="h-full object-contain"
							alt={`${displayUploadedImageIndex}`}
						/>
					</div>
					<div
						class="w-full h-fit my-4 flex flex-row justify-start items-start rounded
						bg-primary-50
						dark:bg-zinc-900"
					>
						<div class="w-full h-full flex flex-col justify-center items-start">
							<div
								class="w-full pl-4 pr-4 text-xs sm:text-sm flex flex-row justify-start items-center"
							></div>
							<div class="h-3"></div>
							<div class="w-full h-fit flex flex-row justify-start items-center">
								<a
									class="w-fit h-6 ml-4 flex flex-row justify-start items-center px-2 py-1 text-cyan-600 dark:text-cyan-400 hover:brightness-150 text-xs font-semibold rounded"
									href={`/canvas?imageUploadId=${displayUploadedImage.upload_id}${journeyId ? `&journeyId=${journeyId}` : ''}`}
									on:click={(e) => {
										e.preventDefault();
										analytics.capture('open_in_canvas_uploaded_image', {
											user_id: user?.uid,
											journey_id: journeyId,
											is_owner: userIsOwner,
											upload_id: displayUploadedImage?.upload_id
										});
										goto(
											`/canvas?imageUploadId=${displayUploadedImage?.upload_id}${journeyId ? `&journeyId=${journeyId}` : ''}`
										);
									}}
								>
									<i class="fas fa-paintbrush mr-2"></i> Open in canvas
								</a>
							</div>
							<div class="h-2"></div>
							<div class="w-full px-2 flex flex-row flex-wrap justify-start items-center text-xs">
								{#if userIsOwner || showGenerationDetails}
									<div
										class="mx-2 mt-1 p-2 border rounded text-xs
										bg-primary-50 text-zinc-900 border-primary-200
										dark:bg-zinc-900 dark:text-zinc-400 dark:border-zinc-800"
									>
										<i class="mr-1 fas fa-calendar text-zinc-900 dark:text-zinc-400"></i>
										{utcTimestampToLocalDate(displayUploadedImage.created_at).toLocaleString()}
									</div>
								{/if}
								<div
									class="mx-2 mt-1 p-2 border rounded text-xs
									bg-primary-50 text-zinc-900 border-primary-200
									dark:bg-zinc-900 dark:text-zinc-400 dark:border-zinc-800"
								>
									<i class="mr-1 fas fa-expand text-zinc-900 dark:text-zinc-400"></i>
									{displayUploadedImage.width} x {displayUploadedImage.height}
								</div>
							</div>
							<div class="h-14"></div>
						</div>
						<div class="w-fit h-fit mr-4 flex flex-col justify-start items-end">
							{#if !isIOS}
								<button
									aria-label="Download image"
									class="w-7 h-7 flex flex-row justify-center items-center text-xs font-semibold rounded border
										text-zinc-900 bg-primary-50 border-primary-200 hover:brightness-90
										dark:text-zinc-400 dark:bg-zinc-800 dark:border-zinc-700 dark:hover:brightness-125 shadow-sm"
									on:click={onPressDownloadUploadedImage}
								>
									<i class="fas fa-download"></i>
								</button>
								<div class="h-1"></div>
							{/if}
							{#if userIsOwner}
								<button
									aria-label="Delete image"
									class="w-7 h-7 flex flex-row justify-center items-center text-xs font-semibold rounded border
										text-zinc-900 bg-primary-50 border-primary-200 hover:brightness-90
										dark:text-zinc-400 dark:bg-zinc-800 dark:border-zinc-700 dark:hover:brightness-125 shadow-sm"
									on:click={onPressDeleteUploadedImage}
								>
									<i class="fas fa-trash"></i>
								</button>
							{/if}
						</div>
					</div>
				{/if}
			</div>
		</div>
	</Transition>
</div>

<ActionNotification bind:this={actionNotification}></ActionNotification>

{#if assetFilter === '3d' && Screenshot3dComponent}
	<svelte:component
		this={Screenshot3dComponent}
		{generatedPointClouds}
		client={pointCloudClient}
		bind:generationIdToImage={pointCloudGenerationIdToImage}
	/>
{/if}

<style>
	.assetContainerHeight {
		height: calc(100vh - 8rem);
		height: calc(100dvh - 8rem);
	}

	textarea::-webkit-scrollbar {
		width: 8px;
		height: 8px;
	}

	textarea::-webkit-scrollbar-track {
		border-radius: 100vh;
		border-radius: 100dvh;
		background: #27272a;
	}

	textarea::-webkit-scrollbar-thumb {
		background: #18181b;
		border-radius: 100vh;
		border-radius: 100dvh;
		border: 1px solid #3f3f46;
	}

	textarea::-webkit-scrollbar-thumb:hover {
		background: #18181b;
	}

	.pin-controls {
		visibility: hidden;
	}

	.library-image-button:hover .pin-controls,
	.pin-controls.active {
		visibility: visible;
	}
</style>
